import { ReactNode, useState } from 'react';
import type { ComboboxItem } from '@screentone/core';
import {
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormLabel,
  Group,
  Input,
  MultiSelect,
  Token,
  Typography,
  useModalPortal,
  Wrapper
} from '@screentone/core';

import {
  Metadata,
  PageModule,
  PublicationSettingSearchableContentType,
  RequiredUiModuleFields,
  UiModuleType
} from 'data/generated/graphql';
import { stringsToComboboxItems } from 'utils/combobox';
import { getModule, getTreatmentType } from 'utils/modules';

const ALL_METADATA_OPTIONS = stringsToComboboxItems(Object.values(Metadata));
const ALL_CONTENT_TYPE_OPTIONS = stringsToComboboxItems(Object.values(PublicationSettingSearchableContentType));

interface TreatmentSettingInfo {
  minRequiredContentItems: number;
  maxRequiredContentItems: number;
  contentTypes: string[];
  optional?: boolean | null;
  message?: string | null;
  ignoreBaseQuery?: boolean | null;
  skipContentParameters?: boolean | null;
  requiredProperties?: string[] | null;
}

interface EditModuleModalProps {
  module: PageModule;
  treatmentSetting: TreatmentSettingInfo | null;
  groupAllowedTreatmentTypes: string[];
  catalogTreatmentTypes?: string[];
  onSave: (updatedModule: PageModule) => void;
  onDismiss: () => void;
}

const toInt = (val: string): number | undefined => {
  const n = parseInt(val, 10);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};

type ActiveModule = {
  title?: string | null;
  titleUrl?: string | null;
  subtitle?: string | null;
  subtitleUrl?: string | null;
  mainText?: string | null;
  secondaryText?: string | null;
  treatmentType?: unknown;
  allowedMeta?: Metadata[] | null;
  contentTypes?: string[] | null;
  showBullets?: boolean | null;
  summaryPriority?: string | null;
  moduleName?: string | null;
  flattenModule?: boolean | null;
} | null;

const LabeledField = ({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) => (
  <Group.Item fullWidth>
    <Group gap="xs" direction="column" fullWidth>
      <FormLabel label={label} labelPosition="top" fullWidth required={required} />
      {children}
    </Group>
  </Group.Item>
);

const SectionDivider = ({ label }: { label: string }) => (
  <>
    <Divider />
    <Group.Item fullWidth>
      <Typography variant="label2" weight="bold" margin={{ bottom: 'sm' }}>
        {label}
      </Typography>
    </Group.Item>
  </>
);

export const EditModuleModal = ({
  module,
  treatmentSetting,
  groupAllowedTreatmentTypes,
  catalogTreatmentTypes = [],
  onSave,
  onDismiss
}: EditModuleModalProps) => {
  const { renderNode } = useModalPortal();
  const activeModule = getModule(module) as ActiveModule;
  const uiModuleType = module.uiModuleType!;

  const [title, setTitle] = useState(activeModule?.title ?? '');
  const [titleUrl, setTitleUrl] = useState(activeModule?.titleUrl ?? '');
  const [subtitle, setSubtitle] = useState(activeModule?.subtitle ?? '');
  const [subtitleUrl, setSubtitleUrl] = useState(activeModule?.subtitleUrl ?? '');
  const [mainText, setMainText] = useState(activeModule?.mainText ?? '');
  const [secondaryText, setSecondaryText] = useState(activeModule?.secondaryText ?? '');
  const [showBullets, setShowBullets] = useState<boolean>(activeModule?.showBullets ?? false);
  const [summaryPriority, setSummaryPriority] = useState(activeModule?.summaryPriority ?? '');
  const [moduleName, setModuleName] = useState(activeModule?.moduleName ?? '');
  const [flattenModule, setFlattenModule] = useState<boolean>(activeModule?.flattenModule ?? false);
  const ttPool = groupAllowedTreatmentTypes.length > 0 ? groupAllowedTreatmentTypes : catalogTreatmentTypes;
  const ttOptions = stringsToComboboxItems(ttPool);
  const [selectedTTs, setSelectedTTs] = useState<ComboboxItem[]>(
    stringsToComboboxItems((activeModule as { allowedTreatmentTypes?: string[] } | null)?.allowedTreatmentTypes ?? [])
  );
  const [selectedAllowedMeta, setSelectedAllowedMeta] = useState<ComboboxItem[]>(
    stringsToComboboxItems(activeModule?.allowedMeta ?? [])
  );
  const [selectedContentTypes, setSelectedContentTypes] = useState<ComboboxItem[]>(
    stringsToComboboxItems(activeModule?.contentTypes ?? treatmentSetting?.contentTypes ?? [])
  );
  const [minItems, setMinItems] = useState(
    module.minItemsOverride != null
      ? String(module.minItemsOverride)
      : treatmentSetting != null
        ? String(treatmentSetting.minRequiredContentItems)
        : ''
  );
  const [maxItems, setMaxItems] = useState(
    module.maxItemsOverride != null
      ? String(module.maxItemsOverride)
      : treatmentSetting != null
        ? String(treatmentSetting.maxRequiredContentItems)
        : ''
  );
  const [ignoreBaseQuery, setIgnoreBaseQuery] = useState<boolean>(
    module.ignoreBaseQuery ?? treatmentSetting?.ignoreBaseQuery ?? false
  );
  const [skipContentParameters, setSkipContentParameters] = useState<boolean>(
    module.skipContentParameters ?? treatmentSetting?.skipContentParameters ?? false
  );
  const [optionalOverride, setOptionalOverride] = useState<boolean>(
    module.optional ?? treatmentSetting?.optional ?? false
  );
  const ALL_REQUIRED_PROPERTIES = stringsToComboboxItems(Object.values(RequiredUiModuleFields));
  const [selectedRequiredProperties, setSelectedRequiredProperties] = useState<ComboboxItem[]>(
    stringsToComboboxItems(module.requiredProperties ?? treatmentSetting?.requiredProperties ?? [])
  );

  const treatmentType = getTreatmentType(module);

  const showSummaryPriority = uiModuleType === UiModuleType.UiTitleModuleType;
  const showModuleName = [UiModuleType.UiBasicOptionalModuleType, UiModuleType.UiTitlePinnedModuleType].includes(
    uiModuleType
  );
  const showFlattenModule = uiModuleType === UiModuleType.UiCollectionModuleType;
  const showAllowedMeta = [UiModuleType.UiTitleModuleType, UiModuleType.UiTitlePinnedModuleType].includes(uiModuleType);
  const showContentConfig =
    uiModuleType !== UiModuleType.UiCollectionModuleType && uiModuleType !== UiModuleType.UiTextModuleType;
  const showTitleSection = [
    UiModuleType.UiTitleModuleType,
    UiModuleType.UiTitlePinnedModuleType,
    UiModuleType.UiRankedModuleType
  ].includes(uiModuleType);
  const showSubtitle = uiModuleType === UiModuleType.UiTitleModuleType;
  const showMainTextSection = uiModuleType === UiModuleType.UiTextModuleType;

  const handleSave = () => {
    const slotTTs = selectedTTs.length > 0 ? selectedTTs.map((t) => String(t.value)) : undefined;
    const resolvedAllowedMeta =
      selectedAllowedMeta.length > 0 ? (selectedAllowedMeta.map((m) => String(m.value)) as Metadata[]) : undefined;
    const existingContentTypes = (activeModule?.contentTypes ?? []) as PublicationSettingSearchableContentType[];
    const resolvedContentTypes =
      selectedContentTypes.length > 0
        ? (selectedContentTypes.map((c) => String(c.value)) as PublicationSettingSearchableContentType[])
        : existingContentTypes;

    const updatedModule: PageModule = {
      ...module,
      minItemsOverride: toInt(minItems),
      maxItemsOverride: toInt(maxItems),
      ignoreBaseQuery,
      skipContentParameters,
      optional: optionalOverride,
      requiredProperties:
        selectedRequiredProperties.length > 0
          ? (selectedRequiredProperties.map((p) => String(p.value)) as RequiredUiModuleFields[])
          : undefined
    };

    switch (uiModuleType) {
      case UiModuleType.UiTitleModuleType:
        updatedModule.uiModuleFields = {
          ...module.uiModuleFields!,
          titleModule: {
            ...module.uiModuleFields!.titleModule!,
            title: title || null,
            titleUrl: titleUrl || null,
            subtitle: subtitle || null,
            subtitleUrl: subtitleUrl || null,
            summaryPriority: summaryPriority || undefined,
            showBullets,
            allowedTreatmentTypes: slotTTs,
            allowedMeta: resolvedAllowedMeta,
            contentTypes: resolvedContentTypes
          }
        };
        break;
      case UiModuleType.UiTitlePinnedModuleType:
        updatedModule.uiModuleFields = {
          ...module.uiModuleFields!,
          titlePinnedModule: {
            ...module.uiModuleFields!.titlePinnedModule!,
            moduleName: moduleName || undefined,
            title: title || null,
            titleUrl: titleUrl || null,
            showBullets,
            allowedTreatmentTypes: slotTTs as never,
            allowedMeta: resolvedAllowedMeta,
            contentTypes: resolvedContentTypes
          }
        };
        break;
      case UiModuleType.UiBasicModuleType:
        updatedModule.uiModuleFields = {
          ...module.uiModuleFields!,
          basicModule: {
            ...module.uiModuleFields!.basicModule!,
            title: title || null,
            titleUrl: titleUrl || null,
            subtitle: subtitle || null,
            subtitleUrl: subtitleUrl || null,
            showBullets,
            allowedTreatmentTypes: slotTTs,
            allowedMeta: resolvedAllowedMeta,
            contentTypes: resolvedContentTypes
          }
        };
        break;
      case UiModuleType.UiRankedModuleType:
        updatedModule.uiModuleFields = {
          ...module.uiModuleFields!,
          rankedModule: {
            ...module.uiModuleFields!.rankedModule!,
            title: title || '',
            showBullets,
            allowedTreatmentTypes: slotTTs as never,
            contentTypes: resolvedContentTypes
          }
        };
        break;
      case UiModuleType.UiTextModuleType:
        updatedModule.uiModuleFields = {
          ...module.uiModuleFields!,
          textModule: {
            ...module.uiModuleFields!.textModule!,
            mainText: mainText || '',
            secondaryText: secondaryText || '',
            showBullets,
            allowedTreatmentTypes: slotTTs as never
          }
        };
        break;
      case UiModuleType.UiBasicOptionalModuleType:
        updatedModule.uiModuleFields = {
          ...module.uiModuleFields!,
          basicOptionalModule: {
            ...module.uiModuleFields!.basicOptionalModule!,
            moduleName: moduleName || undefined,
            showBullets,
            allowedTreatmentTypes: slotTTs as never,
            contentTypes: resolvedContentTypes
          }
        };
        break;
      case UiModuleType.UiCollectionModuleType:
        updatedModule.uiModuleFields = {
          ...module.uiModuleFields!,
          collectionModule: {
            ...module.uiModuleFields!.collectionModule!,
            showBullets,
            flattenModule,
            allowedTreatmentTypes: slotTTs as never
          }
        };
        break;
      case UiModuleType.UiFeedsModuleType:
        updatedModule.uiModuleFields = {
          ...module.uiModuleFields!,
          feedsModule: {
            ...module.uiModuleFields!.feedsModule!,
            showBullets,
            allowedTreatmentTypes: slotTTs as never,
            contentTypes: resolvedContentTypes
          }
        };
        break;
      default:
        break;
    }

    onSave(updatedModule);
    onDismiss();
  };

  return (
    <Dialog status="open" onDismiss={onDismiss} focusTrap={false} renderNode={renderNode}>
      <Dialog.Title>Module Options</Dialog.Title>
      <Dialog.Content>
        <Wrapper padding={{ all: 'md' }}>
          <Group gap="md" direction="column" fullWidth>
            <Group.Item fullWidth>
              <Group gap="xs" align="center" margin={{ bottom: 'xs' }}>
                <Typography variant="label2" weight="bold" margin={{ bottom: 'none' }}>
                  {treatmentType ?? '—'}
                </Typography>
                {treatmentSetting?.optional === true && <Token margin={{ bottom: 'none' }}>Optional</Token>}
              </Group>
              <Typography variant="note" color="asphalt" margin={{ bottom: 'none' }}>
                {uiModuleType}
              </Typography>
              {treatmentSetting?.message && (
                <Typography variant="note" color="asphalt" margin={{ top: 'xs', bottom: 'none' }}>
                  {treatmentSetting.message}
                </Typography>
              )}
            </Group.Item>

            {showTitleSection && (
              <>
                <SectionDivider label="Module Display" />

                <LabeledField label="Title" required={uiModuleType === UiModuleType.UiRankedModuleType}>
                  <Input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
                </LabeledField>

                <LabeledField label="Title URL">
                  <Input type="url" value={titleUrl} onChange={(e) => setTitleUrl(e.target.value)} />
                </LabeledField>

                {showSubtitle && (
                  <>
                    <LabeledField label="Subtitle">
                      <Input type="text" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
                    </LabeledField>

                    <LabeledField label="Subtitle URL">
                      <Input type="url" value={subtitleUrl} onChange={(e) => setSubtitleUrl(e.target.value)} />
                    </LabeledField>
                  </>
                )}
              </>
            )}

            {showMainTextSection && (
              <>
                <SectionDivider label="Module Display" />

                <LabeledField label="Main Text" required>
                  <Input type="text" value={mainText} onChange={(e) => setMainText(e.target.value)} />
                </LabeledField>

                <LabeledField label="Secondary Text" required>
                  <Input type="text" value={secondaryText} onChange={(e) => setSecondaryText(e.target.value)} />
                </LabeledField>
              </>
            )}

            <SectionDivider label="Module Configuration" />

            {ttOptions.length > 0 && (
              <LabeledField label="Allowed treatment types">
                <MultiSelect
                  items={ttOptions}
                  selectedItems={selectedTTs}
                  onSelectedItemsChange={setSelectedTTs}
                  status="ready"
                  emptyContent={<Typography variant="note">No treatment types available</Typography>}
                />
                <Typography variant="note" color="asphalt">
                  Which treatment types the page editor can swap between for this slot. Defaults to all types allowed by
                  the group.
                </Typography>
              </LabeledField>
            )}

            <Group.Item fullWidth>
              <FormLabel label="Show Bullets" labelPosition="right" fullWidth>
                <Checkbox checked={showBullets} onChange={(e) => setShowBullets(e.target.checked)} />
              </FormLabel>
            </Group.Item>

            {showSummaryPriority && (
              <LabeledField label="Summary Priority">
                <Input
                  type="text"
                  value={summaryPriority}
                  onChange={(e) => setSummaryPriority(e.target.value)}
                  placeholder="Overrides issue-level summary priority"
                />
              </LabeledField>
            )}

            {showModuleName && (
              <LabeledField label="Module Name">
                <Input type="text" value={moduleName} onChange={(e) => setModuleName(e.target.value)} />
              </LabeledField>
            )}

            {showFlattenModule && (
              <Group.Item fullWidth>
                <FormLabel label="Flatten Module" labelPosition="right" fullWidth>
                  <Checkbox checked={flattenModule} onChange={(e) => setFlattenModule(e.target.checked)} />
                </FormLabel>
              </Group.Item>
            )}

            <SectionDivider label="Slot Behavior" />

            <Group.Item fullWidth>
              <FormLabel label="Ignore base query" labelPosition="right" fullWidth>
                <Checkbox
                  checked={ignoreBaseQuery}
                  onChange={(e) => setIgnoreBaseQuery(e.target.checked)}
                  data-testid="slot-ignore-base-query"
                />
              </FormLabel>
              <Typography variant="note" color="asphalt">
                Skip the pub-level base query for this slot.
              </Typography>
            </Group.Item>

            <Group.Item fullWidth>
              <FormLabel label="Skip content parameters" labelPosition="right" fullWidth>
                <Checkbox
                  checked={skipContentParameters}
                  onChange={(e) => setSkipContentParameters(e.target.checked)}
                  data-testid="slot-skip-content-parameters"
                />
              </FormLabel>
              <Typography variant="note" color="asphalt">
                Omit content_parameters from Allesseh output for this slot.
              </Typography>
            </Group.Item>

            <Group.Item fullWidth>
              <FormLabel label="Optional" labelPosition="right" fullWidth>
                <Checkbox
                  checked={optionalOverride}
                  onChange={(e) => setOptionalOverride(e.target.checked)}
                  data-testid="slot-optional"
                />
              </FormLabel>
              <Typography variant="note" color="asphalt">
                Whether this slot is optional at publish time.
              </Typography>
            </Group.Item>

            <LabeledField label="Required properties">
              <MultiSelect
                items={ALL_REQUIRED_PROPERTIES}
                selectedItems={selectedRequiredProperties}
                onSelectedItemsChange={setSelectedRequiredProperties}
                status="ready"
                emptyContent={<Typography variant="note">No properties available</Typography>}
                data-testid="slot-required-properties"
              />
              {treatmentSetting?.requiredProperties && treatmentSetting.requiredProperties.length > 0 && (
                <Typography variant="note" color="asphalt">
                  TT setting: {treatmentSetting.requiredProperties.join(', ')}
                </Typography>
              )}
              <Typography variant="note" color="asphalt">
                Metadata fields that must be filled on content items in this slot before the page can be published.
              </Typography>
            </LabeledField>

            {showContentConfig && (
              <>
                <SectionDivider label="Content Configuration" />

                {showAllowedMeta && (
                  <LabeledField label="Allowed metadata fields">
                    <MultiSelect
                      items={ALL_METADATA_OPTIONS}
                      selectedItems={selectedAllowedMeta}
                      onSelectedItemsChange={setSelectedAllowedMeta}
                      status="ready"
                      emptyContent={<Typography variant="note">No metadata fields available</Typography>}
                    />
                  </LabeledField>
                )}

                <LabeledField label="Content types">
                  <MultiSelect
                    items={ALL_CONTENT_TYPE_OPTIONS}
                    selectedItems={selectedContentTypes}
                    onSelectedItemsChange={setSelectedContentTypes}
                    status="ready"
                    emptyContent={<Typography variant="note">No content types available</Typography>}
                  />
                  {treatmentSetting && treatmentSetting.contentTypes.length > 0 && (
                    <Typography variant="note" color="asphalt">
                      TT setting: {treatmentSetting.contentTypes.join(', ')}
                    </Typography>
                  )}
                </LabeledField>

                <LabeledField label="Min items">
                  <Input
                    type="number"
                    value={minItems}
                    onChange={(e) => setMinItems(e.target.value)}
                    placeholder={treatmentSetting != null ? String(treatmentSetting.minRequiredContentItems) : ''}
                  />
                </LabeledField>

                <LabeledField label="Max items">
                  <Input
                    type="number"
                    value={maxItems}
                    onChange={(e) => setMaxItems(e.target.value)}
                    placeholder={treatmentSetting != null ? String(treatmentSetting.maxRequiredContentItems) : ''}
                  />
                </LabeledField>
              </>
            )}
          </Group>
        </Wrapper>
        <Divider />
        <Wrapper padding={{ all: 'md' }}>
          <Group gap="sm" justify="flex-end">
            <Button onClick={onDismiss} secondary>
              Cancel
            </Button>
            <Button onClick={handleSave} primary>
              Save
            </Button>
          </Group>
        </Wrapper>
      </Dialog.Content>
    </Dialog>
  );
};
